import React, { useMemo, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import updateLocale from "dayjs/plugin/updateLocale";
import "dayjs/locale/en-gb";
import {
    Box,
    InputAdornment,
    Popover,
    TextField,
    Typography
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { PickersDay, PickersDayProps } from "@mui/x-date-pickers/PickersDay";

dayjs.locale("en-gb");
dayjs.extend(customParseFormat);
dayjs.extend(updateLocale);

type DateOption = string | Date | Dayjs | null | undefined;

interface OverlayWeekPickerProps {
    label: string;
    value: DateOption;
    onChange: (value: string) => void;
    weekStartDateOptions?: DateOption[];
    weekNumberBaseDateOption?: DateOption;
    disabled?: boolean;
}

interface WeekPickerDayProps extends PickersDayProps<Dayjs> {
    selectedWeekStart?: Dayjs | null;
    hoveredWeekStart?: Dayjs | null;
    onDayHover?: (weekStart: Dayjs) => void;
    weekStartDay?: number;
    firstWeekStart?: Dayjs | null;
    weekStartDateOptions?: string[];
}

const MONDAY_DAY_INDEX = 1;

const getStartOfForecastWeek = (date: Dayjs | null, weekStartDay = MONDAY_DAY_INDEX): Dayjs | null => {
    if (!date) {
        return null;
    }

    const normalizedWeekStartDay = Number.isInteger(weekStartDay) && weekStartDay >= 0 && weekStartDay <= 6
        ? weekStartDay
        : MONDAY_DAY_INDEX;

    return date.startOf("day").subtract((date.day() - normalizedWeekStartDay + 7) % 7, "day");
};

const parseDateOption = (dateOption: DateOption): Dayjs | null => {
    if (!dateOption) {
        return null;
    }

    if (dayjs.isDayjs(dateOption)) {
        return dateOption;
    }

    if (dateOption instanceof Date) {
        const parsedDate = dayjs(dateOption);
        return parsedDate.isValid() ? parsedDate : null;
    }

    const parsed = dayjs(dateOption, ["DD-MM-YYYY", "YYYY-MM-DD", "DD/MM/YY", "DD/MM/YYYY"], true);
    if (parsed.isValid()) {
        return parsed;
    }

    const fallbackParsed = dayjs(dateOption);
    return fallbackParsed.isValid() ? fallbackParsed : null;
};

const formatDateOption = (date: Dayjs): string => date.format("DD-MM-YYYY");

const getForecastWeekNumber = (date: Dayjs, firstWeekStart: Dayjs): number => {
    const startOfDay = date.startOf("day");
    const startOfFirstWeek = firstWeekStart.startOf("day");
    return Math.floor(startOfDay.diff(startOfFirstWeek, "day") / 7) + 1;
};

export const normalizeWeekStartDateOptions = (weekStartDateOptions: DateOption[] = []): string[] => {
    const seen = new Set<string>();
    const normalized: string[] = [];

    weekStartDateOptions.forEach((dateOption) => {
        const parsed = parseDateOption(dateOption);
        if (!parsed) {
            return;
        }

        const normalizedDate = formatDateOption(parsed);
        if (seen.has(normalizedDate)) {
            return;
        }

        seen.add(normalizedDate);
        normalized.push(normalizedDate);
    });

    return normalized;
};

export const getWeekStartOptionForDate = (
    date: Dayjs | null,
    weekStartDateOptions: string[],
    weekStartDay = MONDAY_DAY_INDEX
): string | null => {
    if (!date || !weekStartDateOptions?.length) {
        return null;
    }

    const startOfWeek = getStartOfForecastWeek(date, weekStartDay);
    if (!startOfWeek) {
        return null;
    }

    const startOfWeekString = formatDateOption(startOfWeek);
    return weekStartDateOptions.includes(startOfWeekString) ? startOfWeekString : null;
};

export const getForecastWeekStartDay = (weekStartDateOptions: DateOption[] = [], fallbackValue: DateOption = null): number => {
    const dayOfWeekCounts = [0, 0, 0, 0, 0, 0, 0];

    weekStartDateOptions.forEach((dateOption) => {
        const parsed = parseDateOption(dateOption);
        if (!parsed) {
            return;
        }

        dayOfWeekCounts[parsed.day()] += 1;
    });

    if (fallbackValue) {
        const fallbackParsed = parseDateOption(fallbackValue);
        if (fallbackParsed) {
            dayOfWeekCounts[fallbackParsed.day()] += 1;
        }
    }

    let resolvedDay = MONDAY_DAY_INDEX;
    let maxCount = 0;

    dayOfWeekCounts.forEach((count, day) => {
        if (count > maxCount) {
            maxCount = count;
            resolvedDay = day;
        }
    });

    return maxCount > 0 ? resolvedDay : MONDAY_DAY_INDEX;
};

export const formatWeekLabel = (dateOption: DateOption): string => {
    const date = parseDateOption(dateOption);
    return date ? `Week beginning ${date.format("DD/MM/YY")}` : "";
};

const WeekPickerDay = (props: WeekPickerDayProps) => {
    const {
        day,
        outsideCurrentMonth,
        selectedWeekStart,
        hoveredWeekStart,
        onDayHover,
        weekStartDay = MONDAY_DAY_INDEX,
        firstWeekStart = null,
        weekStartDateOptions = [],
        ...other
    } = props;
    const weekStart = getStartOfForecastWeek(day, weekStartDay);
    const isInSelectedWeek = Boolean(selectedWeekStart && weekStart?.isSame(selectedWeekStart, "day"));
    const isInHoveredWeek = Boolean(!isInSelectedWeek && hoveredWeekStart && weekStart?.isSame(hoveredWeekStart, "day"));
    const isStartOfWeek = Boolean(weekStart && day.isSame(weekStart, "day"));
    const isEndOfWeek = Boolean(weekStart && day.isSame(weekStart.add(6, "day"), "day"));
    const isInActiveWeek = isInSelectedWeek || isInHoveredWeek;
    const weekStartOption = weekStart ? formatDateOption(weekStart) : null;
    const isValidWeekStart = Boolean(weekStartOption && weekStartDateOptions.includes(weekStartOption));
    const weekNumber = weekStart && firstWeekStart && isValidWeekStart ? getForecastWeekNumber(weekStart, firstWeekStart) : null;

    return (
        <PickersDay
            {...other}
            day={day}
            outsideCurrentMonth={outsideCurrentMonth}
            disableMargin
            onMouseEnter={(event) => {
                other.onMouseEnter?.(event, day);
                if (!other.disabled && weekStart) {
                    onDayHover?.(weekStart);
                }
            }}
            sx={{
                ...(isInActiveWeek && {
                    borderRadius: 0
                }),
                ...(isInSelectedWeek && {
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    "&:hover, &:focus": {
                        bgcolor: "primary.dark"
                    }
                }),
                ...(isInHoveredWeek && {
                    bgcolor: "rgba(25, 118, 210, 0.16)",
                    color: "text.primary",
                    "&:hover, &:focus": {
                        bgcolor: "rgba(25, 118, 210, 0.22)"
                    }
                }),
                ...((isInSelectedWeek || isInHoveredWeek) && isStartOfWeek && {
                    borderTopLeftRadius: "999px",
                    borderBottomLeftRadius: "999px"
                }),
                ...((isInSelectedWeek || isInHoveredWeek) && isEndOfWeek && {
                    borderTopRightRadius: "999px",
                    borderBottomRightRadius: "999px"
                }),
                ...(isStartOfWeek && weekNumber !== null && {
                    overflow: "visible",
                    "&::before": {
                        content: `"${weekNumber}"`,
                        position: "absolute",
                        left: -30,
                        top: "50%",
                        transform: "translateY(-50%)",
                        fontSize: "0.65rem",
                        fontWeight: 600,
                        letterSpacing: "0.04em",
                        color: "rgba(25, 118, 210, 0.9)",
                        backgroundColor: "rgba(25, 118, 210, 0.08)",
                        border: "1px solid rgba(25, 118, 210, 0.25)",
                        borderRadius: "999px",
                        width: 24,
                        textAlign: "center",
                        lineHeight: "16px",
                        height: 18,
                        pointerEvents: "none"
                    }
                }),
                transition: "background-color 120ms ease-in-out",
                position: "relative"
            }}
        />
    );
};

const OverlayWeekPicker: React.FC<OverlayWeekPickerProps> = ({
    label,
    value,
    onChange,
    weekStartDateOptions = [],
    weekNumberBaseDateOption = null,
    disabled = false
}) => {
    const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
    const [hoveredWeekStart, setHoveredWeekStart] = useState<Dayjs | null>(null);
    const normalizedWeekStartDateOptions = useMemo(
        () => normalizeWeekStartDateOptions(weekStartDateOptions),
        [weekStartDateOptions]
    );
    const weekStartDay = useMemo(
        () => getForecastWeekStartDay(normalizedWeekStartDateOptions, value),
        [normalizedWeekStartDateOptions, value]
    );
    const adapterLocale = useMemo(() => {
        dayjs.updateLocale("en-gb", { weekStart: weekStartDay });
        return "en-gb";
    }, [weekStartDay]);
    const selectedWeekStart = useMemo(() => parseDateOption(value), [value]);
    const fallbackDate = useMemo(() => parseDateOption(normalizedWeekStartDateOptions[0]), [normalizedWeekStartDateOptions]);
    const firstWeekStart = useMemo(() => {
        const explicitBase = parseDateOption(weekNumberBaseDateOption);
        if (explicitBase) {
            return explicitBase;
        }

        return normalizedWeekStartDateOptions.length ? parseDateOption(normalizedWeekStartDateOptions[0]) : null;
    }, [weekNumberBaseDateOption, normalizedWeekStartDateOptions]);
    const calendarValue = selectedWeekStart || fallbackDate || dayjs();

    const handleCalendarChange = (newDate: Dayjs | null) => {
        const matchedWeekStart = getWeekStartOptionForDate(newDate, normalizedWeekStartDateOptions, weekStartDay);
        if (!matchedWeekStart) {
            return;
        }

        onChange(matchedWeekStart);
        setAnchorElement(null);
        setHoveredWeekStart(null);
    };

    const shouldDisableDate = (date: Dayjs) => !getWeekStartOptionForDate(date, normalizedWeekStartDateOptions, weekStartDay);

    return (
        <Box sx={{ mt: 1 }}>
            <TextField
                margin="dense"
                label={label}
                fullWidth
                autoComplete="off"
                value={formatWeekLabel(value)}
                placeholder="Select a week"
                onClick={(event) => {
                    if (disabled) {
                        return;
                    }
                    setAnchorElement(event.currentTarget);
                }}
                disabled={disabled}
                InputProps={{
                    readOnly: true,
                    endAdornment: (
                        <InputAdornment position="end">
                            <CalendarMonthIcon color="action" />
                        </InputAdornment>
                    )
                }}
            />
            <Popover
                open={Boolean(anchorElement)}
                anchorEl={anchorElement}
                onClose={() => {
                    setAnchorElement(null);
                    setHoveredWeekStart(null);
                }}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            >
                <Box
                    sx={{
                        p: 2,
                        background: "linear-gradient(180deg, rgba(25, 118, 210, 0.08) 0%, rgba(25, 118, 210, 0.02) 100%)"
                    }}
                    onMouseLeave={() => setHoveredWeekStart(null)}
                >
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Select a week
                    </Typography>
                    <LocalizationProvider key={weekStartDay} dateAdapter={AdapterDayjs} adapterLocale={adapterLocale}>
                        <DateCalendar
                            value={calendarValue}
                            onChange={handleCalendarChange}
                            shouldDisableDate={shouldDisableDate}
                            referenceDate={fallbackDate || dayjs()}
                            showDaysOutsideCurrentMonth
                            slots={{ day: WeekPickerDay }}
                            slotProps={{
                                day: {
                                    selectedWeekStart,
                                    hoveredWeekStart,
                                    onDayHover: (weekStart) => setHoveredWeekStart(weekStart),
                                    weekStartDay,
                                    firstWeekStart,
                                    weekStartDateOptions: normalizedWeekStartDateOptions
                                } as Partial<WeekPickerDayProps>
                            }}
                        />
                    </LocalizationProvider>
                </Box>
            </Popover>
        </Box>
    );
};

export default OverlayWeekPicker;
